<template>
  <div class="px-[24px] h-screen flex flex-col justify-center">
    <div class="">
      <MainTitle>
        {{ $t('login_title') }}
      </MainTitle>

      <SocialAuth />

      <Divider class="my-5" />

      <SocialAuthUsed
        v-if="useProvider"
        :provider="useProvider"
        class="mt-4"
      />

      <EmailInput
        v-model="state.email"
        v-model:valid="state.emailIsValid"
        class="mt-4"
      />

      <PasswordInput
        v-model="state.password"
        :validation-enabled="false"
      />

      <ErrorWidget v-if="hasError">
        {{ $t('wrong_password') }}
      </ErrorWidget>

      <ErrorWidget v-if="authError">
        {{ $t('error_general') }}
      </ErrorWidget>

      <SmallLink
        class="my-4"
        @click="goToResetPassword"
      >
        {{ $t('forgot_password') }}
      </SmallLink>

      <BaseButton
        :disabled="!canLogin"
        @click="loginAction"
      >
        {{ $t('login_action') }}
      </BaseButton>

      <BottomLink
        class="mt-4"
        @click="goToRegister"
      >
        <template #title>
          {{ $t('no_account') }}
        </template>
        <template #link>
          {{ $t('register_link') }}
        </template>
      </BottomLink>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, computed } from 'vue'
import SocialAuth from '@/components/buttons/social-auth/SocialAuth.vue'
import Divider from '@/components/ui/Divider.vue'
import SocialAuthUsed from '@/components/ui/SocialAuthUsed.vue'
import MainTitle from '@/components/ui/MainTitle.vue'
import PasswordInput from '@/components/inputs/PasswordInput.vue'
import SmallLink from '@/components/ui/SmallLink.vue'
import BottomLink from '@/components/ui/BottomLink.vue'
import BaseButton from '@/components/buttons/BaseButton.vue'
import EmailInput from '@/components/inputs/EmailInput.vue'
import ErrorWidget from '@/components/ui/ErrorWidget.vue'
import { useRouter } from 'vue-router'
import { ROUTE_NAMES } from '@/router'
import { appType } from '@/comp/AppType'
import { getPrefilledAuthCredentials } from '@/config/developmentAuthCredentials'
import { authorizeUserViaEmailAndPassword } from '@/services/api/Auth'
import { logAuthEvent } from '@/services/debug/authDebug'

import { redirectToAppUrlWithAuthorizationCode } from '@/services/auth/Auth.service'

const router = useRouter()

const state = reactive({
  email: '',
  password: '',
  emailIsValid: false
})

const applyPrefilledCredentials = () => {
  const prefilledCredentials = getPrefilledAuthCredentials(appType.value)

  if (!prefilledCredentials) return

  state.email = prefilledCredentials.email
  state.password = prefilledCredentials.password
}

function getQueryParams() {
  const params = new URLSearchParams(window.location.search)
  const paramsObject = {}

  for (const [key, value] of params) {
    paramsObject[key] = value
  }

  return paramsObject
}

const urlQueryParams = getQueryParams()
const authError = ref(!!urlQueryParams?.errorCode)

const loading = ref(false)
const hasError = ref(false)
const useProvider = ref(false)
const serErrorFalse = () => {
  hasError.value = false
}

watch(() => state, serErrorFalse, { deep: true })
watch(appType, applyPrefilledCredentials, { immediate: true })

const loginAction = async () => {
  authError.value = false
  loading.value = true
  logAuthEvent('auth.login.submit.started', {
    emailProvided: Boolean(state.email),
    passwordProvided: Boolean(state.password)
  })

  const { data, statusCode, response, error } = await authorizeUserViaEmailAndPassword({
    email: state.email,
    password: state.password
  })

  loading.value = false

  try {
    const responseBody = response.value ? await response.value.json() : null
    if (responseBody.status === 'UserExistsForOtherIdentityProvider') {
      if (!responseBody?.userIdentityProviderTypes?.includes('Google')) useProvider.value = 'Apple'
      else useProvider.value = 'Google'
      logAuthEvent('auth.login.submit.provider-mismatch', {
        suggestedProvider: useProvider.value || ''
      })
      return
    }
  } catch (e) {
    logAuthEvent(
      'auth.login.submit.provider-mismatch-parse-failed',
      {
        error: e
      },
      'warn'
    )
  }

  if (statusCode.value === 401) {
    hasError.value = true
    logAuthEvent(
      'auth.login.submit.invalid-credentials',
      {
        statusCode: statusCode.value
      },
      'warn'
    )
    return
  }

  if (error.value) {
    hasError.value = true
    logAuthEvent(
      'auth.login.submit.failed',
      {
        statusCode: statusCode.value,
        hasAuthorizationCode: Boolean(data.value?.authorizationCode),
        error: error.value
      },
      'error'
    )
    return
  }

  logAuthEvent('auth.login.submit.completed', {
    hasAuthorizationCode: Boolean(data.value?.authorizationCode)
  })
  redirectToAppUrlWithAuthorizationCode(data.value?.authorizationCode)
}

const canLogin = computed(() => !loading.value && state.emailIsValid && state.password.length > 0)

const goToResetPassword = () => router.push({ name: ROUTE_NAMES.PASSWORD_RESET })
const goToRegister = () => router.push({ name: ROUTE_NAMES.REGISTER })
</script>
